<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { type ClinicalVoidItemType } from '@/api/clinical-record-voids';
import { type ClinicalCorrectionKind } from '@/utils/clinical-void';
import ClinicalRecordVoidDialog from './clinical-record-void-dialog.vue';

// The "Anular" button of a registro clínico, shown only when the user may
// anular it now (the serializers' `canVoid`). The dialog mounts on first open,
// so a long list adds no dialog per row.
const props = withDefaults(defineProps<{
  itemType: ClinicalVoidItemType;
  itemId: number;
  // What the registro is, for the button name and the dialog.
  label: string;
  canVoid?: boolean;
  correction?: ClinicalCorrectionKind | null;
  currentText?: string | null;
}>(), { canVoid: false, correction: null, currentText: null });

const emit = defineEmits<{ voided: [] }>();

const open = ref(false);
const mounted = ref(false);
const button = ref<{ $el: HTMLElement } | null>(null);

function show() {
  mounted.value = true;
  open.value = true;
}

// The dialog opens without an activator, so focus goes back to the button by
// hand (when the registro is still listed).
watch(open, async (isOpen) => {
  if (isOpen) return;
  await nextTick();
  button.value?.$el?.focus();
});
</script>
<template>
  <span
    v-if="props.canVoid"
    class="d-inline-flex align-center"
  >
    <v-btn
      ref="button"
      icon
      size="small"
      variant="text"
      density="comfortable"
      :aria-label="`Anular (${props.label})`"
      @click.stop="show"
    >
      <v-icon
        icon="mdi-cancel"
        size="small"
      />
      <v-tooltip
        activator="parent"
        location="top"
      >
        Anular
      </v-tooltip>
    </v-btn>
    <clinical-record-void-dialog
      v-if="mounted"
      v-model="open"
      :item-type="props.itemType"
      :item-id="props.itemId"
      :label="props.label"
      :correction="props.correction"
      :current-text="props.currentText"
      @voided="emit('voided')"
    />
  </span>
</template>
